import moment from 'moment'
import 'moment/locale/id'

// Set Indonesian locale
moment.locale('id')

/**
 * Composable for date formatting utilities
 * Provides consistent date formatting across the application
 */
export function useDateFormatting() {
  /**
   * Standard date formats used in the application
   */
  const DATE_FORMATS = {
    DATE_ONLY: 'DD-MM-YYYY', // 01-12-2025
    DATE_TIME: 'DD-MM-YYYY HH:mm', // 01-12-2025 14:30
    DATE_DISPLAY: 'DD MMMM YYYY', // 01 Desember 2025
    DATE_TIME_DISPLAY: 'DD MMMM YYYY HH:mm', // 01 Desember 2025 14:30
    TIME_ONLY: 'HH:mm', // 14:30
    ISO: 'YYYY-MM-DD', // 2025-12-01
  }

  /**
   * Format date for API submission (DD-MM-YYYY)
   * @param {moment.Moment|Date|string} date - The date to format
   * @returns {string|null} - Formatted date string or null
   */
  const formatForAPI = date => {
    if (!date) return null
    return moment(date).format(DATE_FORMATS.DATE_ONLY)
  }

  /**
   * Format datetime for API submission (DD-MM-YYYY HH:mm)
   * @param {moment.Moment|Date|string} datetime - The datetime to format
   * @returns {string|null} - Formatted datetime string or null
   */
  const formatDateTimeForAPI = datetime => {
    if (!datetime) return null
    console.log('Formatting datetime for API:', moment(datetime).format(DATE_FORMATS.DATE_TIME))
    return moment(datetime).format(DATE_FORMATS.DATE_TIME)
  }

  /**
   * Format date for display (DD MMMM YYYY) - Indonesian
   * @param {moment.Moment|Date|string} date - The date to format
   * @returns {string} - Formatted date string or '-'
   */
  const formatForDisplay = date => {
    if (!date) return '-'
    return moment(date).format(DATE_FORMATS.DATE_DISPLAY)
  }

  /**
   * Format datetime for display (DD MMMM YYYY HH:mm) - Indonesian
   * @param {moment.Moment|Date|string} datetime - The datetime to format
   * @returns {string} - Formatted datetime string or '-'
   */
  const formatDateTimeForDisplay = datetime => {
    if (!datetime) return '-'
    return moment(datetime).format(DATE_FORMATS.DATE_TIME_DISPLAY)
  }

  /**
   * Parse date string from API format (DD-MM-YYYY) to moment
   * @param {string} dateStr - The date string to parse
   * @returns {moment.Moment|null} - Parsed moment object or null
   */
  const parseFromAPI = dateStr => {
    if (!dateStr) return null
    return moment(dateStr, DATE_FORMATS.DATE_ONLY)
  }

  /**
   * Parse datetime string from API format (DD-MM-YYYY HH:mm) to moment
   * @param {string} datetimeStr - The datetime string to parse
   * @returns {moment.Moment|null} - Parsed moment object or null
   */
  const parseDateTimeFromAPI = datetimeStr => {
    if (!datetimeStr) return null
    return moment(datetimeStr, DATE_FORMATS.DATE_TIME)
  }

  /**
   * Get relative time from now (e.g., "2 hari yang lalu")
   * @param {moment.Moment|Date|string} date - The date to compare
   * @returns {string} - Relative time string
   */
  const getRelativeTime = date => {
    if (!date) return '-'
    return moment(date).fromNow()
  }

  /**
   * Check if a date is today
   * @param {moment.Moment|Date|string} date - The date to check
   * @returns {boolean}
   */
  const isToday = date => {
    if (!date) return false
    return moment(date).isSame(moment(), 'day')
  }

  /**
   * Check if a date is in the past
   * @param {moment.Moment|Date|string} date - The date to check
   * @returns {boolean}
   */
  const isPast = date => {
    if (!date) return false
    return moment(date).isBefore(moment(), 'day')
  }

  /**
   * Get current date as moment object
   * @returns {moment.Moment}
   */
  const now = () => moment()

  /**
   * Get Roman numeral for month (I-XII)
   * @param {number} month - Month number (1-12)
   * @returns {string} - Roman numeral
   */
  const getRomanMonth = month => {
    const romanNumerals = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII']
    return romanNumerals[month - 1] || ''
  }

  /**
   * Get current month as Roman numeral
   * @returns {string}
   */
  const getCurrentRomanMonth = () => {
    return getRomanMonth(moment().month() + 1)
  }

  /**
   * Get current year
   * @returns {string}
   */
  const getCurrentYear = () => {
    return moment().format('YYYY')
  }

  return {
    // Constants
    DATE_FORMATS,

    // Format methods
    formatForAPI,
    formatDateTimeForAPI,
    formatForDisplay,
    formatDateTimeForDisplay,

    // Parse methods
    parseFromAPI,
    parseDateTimeFromAPI,

    // Utility methods
    getRelativeTime,
    isToday,
    isPast,
    now,
    getRomanMonth,
    getCurrentRomanMonth,
    getCurrentYear,
  }
}
